import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatXOF } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Users, UserCheck, CalendarCheck, Wallet, Activity, Check, XCircle, MessageCircle, Video, Headphones, MessageSquare, PlayCircle, Calendar, Clock } from "lucide-react";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";

const STATUS_LABEL = {
  pending: "En attente",
  confirmed: "Confirmé",
  completed: "Terminé",
  cancelled: "Annulé",
};
const STATUS_STYLE = {
  pending: "bg-gold-light/40 text-gold-dark border border-gold/40",
  confirmed: "bg-sage-50 text-sage-700 border border-sage-200",
  completed: "bg-beige border border-border text-ink-soft",
  cancelled: "bg-red-50 text-red-700 border border-red-200",
};
const MODE_ICON = { video: Video, audio: Headphones, chat: MessageSquare };
const MODE_LABEL = { video: "Vidéo", audio: "Audio", chat: "Chat" };

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [appts, setAppts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [myAppts, setMyAppts] = useState([]);
  const [myAcc, setMyAcc] = useState(null);

  const fetchAll = async () => {
    try {
      const [s, u, a, p, n, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/appointments"),
        api.get("/admin/payments"),
        api.get("/admin/notifications"),
        api.get("/me/accompagnateur-appointments").catch(() => ({ data: { items: [], accompagnateur: null } })),
      ]);
      setStats(s.data);
      setUsers(u.data.items || []);
      setAppts(a.data.items || []);
      setPayments(p.data.items || []);
      setNotifs(n.data.items || []);
      setMyAppts(m.data.items || []);
      setMyAcc(m.data.accompagnateur || null);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { fetchAll(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/appointments/${id}/status`, { status });
    fetchAll();
  };

  const validatePayment = async (pid, status) => {
    await api.put(`/admin/payments/${pid}`, { status });
    fetchAll();
  };

  return (
    <div className="container-jamm py-12">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Administration</div>
          <h1 className="h-display mt-3">Tableau de bord</h1>
          <p className="lead mt-2">Pilotage de la plateforme : utilisateurs, accompagnateurs, séances et revenus.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
        {stats && [
          { label: "Utilisateurs", value: stats.users, icon: Users },
          { label: "Accompagnateurs", value: stats.accompagnateurs, icon: UserCheck },
          { label: "Séances", value: stats.appointments_total, icon: CalendarCheck },
          { label: "À confirmer", value: stats.appointments_pending, icon: Activity },
          { label: "Revenus", value: formatXOF(stats.revenue), icon: Wallet },
        ].map((s) => (
          <div key={s.label} className="card-soft" data-testid={`admin-stat-${s.label}`}>
            <div className="flex items-center justify-between">
              <s.icon className="w-5 h-5 text-sage-600" strokeWidth={1.5} />
            </div>
            <div className="text-ink-mute text-xs uppercase tracking-wider mt-3">{s.label}</div>
            <div className="font-display text-3xl text-ink mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue={myAppts.length > 0 || myAcc ? "mine" : "appts"} className="mt-10">
        <TabsList className="bg-beige rounded-full p-1 flex-wrap">
          {myAcc && (
            <TabsTrigger value="mine" className="rounded-full px-5 py-2" data-testid="admin-tab-mine">
              Mes rendez-vous
              {myAppts.filter((x) => x.status === "pending" || x.status === "confirmed").length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-sage-500 text-white text-[10px] font-semibold">
                  {myAppts.filter((x) => x.status === "pending" || x.status === "confirmed").length}
                </span>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="appts" className="rounded-full px-5 py-2" data-testid="admin-tab-appts">Tous les rendez-vous</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-full px-5 py-2" data-testid="admin-tab-payments">Paiements</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full px-5 py-2" data-testid="admin-tab-users">Utilisateurs</TabsTrigger>
        </TabsList>

        {myAcc && (
          <TabsContent value="mine" className="mt-6" data-testid="admin-mine-tab">
            <div className="card-soft">
              <div className="flex items-center gap-3 mb-2">
                {myAcc.avatar_url && <img src={myAcc.avatar_url} alt={myAcc.name} className="w-10 h-10 rounded-full object-cover ring-2 ring-sage-100" />}
                <div>
                  <div className="font-display text-xl text-ink">Vos séances — {myAcc.name}</div>
                  <p className="text-ink-soft text-sm">Démarrez vos séances vidéo/audio directement depuis cette liste. Le salon Jitsi s'ouvre en sécurité.</p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-3" data-testid="admin-mine-list">
              {(() => {
                const upcoming = myAppts.filter((a) => a.status === "pending" || a.status === "confirmed");
                const past = myAppts.filter((a) => a.status === "completed" || a.status === "cancelled");

                if (myAppts.length === 0) {
                  return (
                    <div className="card-soft text-center py-12 text-ink-soft" data-testid="admin-mine-empty">
                      <Calendar className="w-7 h-7 text-ink-mute mx-auto mb-2" />
                      Aucune séance pour le moment. Quand un client réservera avec vous, sa séance apparaîtra ici.
                    </div>
                  );
                }

                return (
                  <>
                    {upcoming.length > 0 && (
                      <>
                        <div className="overline text-gold mt-2">À venir ({upcoming.length})</div>
                        {upcoming.map((ap) => {
                          const Icon = MODE_ICON[ap.mode] || Video;
                          const canStart = ap.mode !== "chat" && ap.status === "confirmed" && ap.jitsi_url;
                          return (
                            <div key={ap.id} className="card-soft flex flex-wrap items-center justify-between gap-4" data-testid={`admin-mine-${ap.id}`}>
                              <div className="flex items-center gap-4 min-w-0">
                                <div className="grid place-items-center w-11 h-11 rounded-2xl bg-sage-50 text-sage-700"><Icon className="w-5 h-5" strokeWidth={1.5} /></div>
                                <div className="min-w-0">
                                  <div className="font-display text-lg text-ink truncate">{ap.anonymous ? "Client anonyme" : (ap.user_name || "Client")}</div>
                                  <div className="text-ink-soft text-sm flex flex-wrap items-center gap-3">
                                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {ap.date}</span>
                                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {ap.time} · {ap.duration_min} min</span>
                                    <span className="capitalize">{MODE_LABEL[ap.mode] || ap.mode}</span>
                                  </div>
                                  {ap.note && <div className="text-xs text-ink-mute mt-1 italic line-clamp-1">« {ap.note} »</div>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_STYLE[ap.status]}`}>{STATUS_LABEL[ap.status]}</span>
                                <span className="font-display text-lg text-sage-700">{formatXOF(ap.price)}</span>
                                {canStart ? (
                                  <a
                                    href={ap.jitsi_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-primary !py-1.5 !px-4 text-xs"
                                    data-testid={`admin-start-${ap.id}`}
                                  >
                                    <PlayCircle className="w-3.5 h-3.5" /> Démarrer
                                  </a>
                                ) : ap.mode === "chat" && ap.status === "confirmed" ? (
                                  <a
                                    href={`https://wa.me/?text=${encodeURIComponent(`Bonjour, je suis ${myAcc.name} de Sama Écoute. On commence notre séance chat prévue à ${ap.time}.`)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-pill bg-[#25D366] text-white !py-1.5 !px-4 text-xs hover:bg-[#1bb056]"
                                    data-testid={`admin-chat-${ap.id}`}
                                  >
                                    <MessageSquare className="w-3.5 h-3.5" /> Chat WhatsApp
                                  </a>
                                ) : ap.status === "pending" ? (
                                  <button
                                    onClick={() => setStatus(ap.id, "confirmed")}
                                    className="btn-outline !py-1.5 !px-4 text-xs"
                                    data-testid={`admin-confirm-${ap.id}`}
                                  >
                                    <Check className="w-3.5 h-3.5" /> Confirmer
                                  </button>
                                ) : null}
                                {ap.status === "confirmed" && (
                                  <button
                                    onClick={() => setStatus(ap.id, "completed")}
                                    className="btn-outline !py-1.5 !px-4 text-xs"
                                    data-testid={`admin-complete-${ap.id}`}
                                    title="Marquer comme terminé"
                                  >
                                    Terminer
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    {past.length > 0 && (
                      <>
                        <div className="overline text-ink-mute mt-6">Historique ({past.length})</div>
                        {past.map((ap) => (
                          <div key={ap.id} className="card-soft flex flex-wrap items-center justify-between gap-4 opacity-80" data-testid={`admin-mine-history-${ap.id}`}>
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="grid place-items-center w-10 h-10 rounded-2xl bg-beige text-ink-soft text-xs uppercase">{MODE_LABEL[ap.mode]?.[0] || "S"}</div>
                              <div className="min-w-0">
                                <div className="font-display text-base text-ink truncate">{ap.anonymous ? "Client anonyme" : (ap.user_name || "Client")}</div>
                                <div className="text-ink-mute text-xs">{ap.date} · {ap.time} · {ap.duration_min} min</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_STYLE[ap.status]}`}>{STATUS_LABEL[ap.status]}</span>
                              <span className="text-ink-soft text-sm">{formatXOF(ap.price)}</span>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </TabsContent>
        )}

        <TabsContent value="appts" className="mt-6">
          <div className="card-soft p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-beige/60 text-ink-soft uppercase text-xs tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Client</th>
                    <th className="text-left px-5 py-3">Accompagnateur</th>
                    <th className="text-left px-5 py-3">Date</th>
                    <th className="text-left px-5 py-3">Durée</th>
                    <th className="text-left px-5 py-3">Statut</th>
                    <th className="text-left px-5 py-3">Prix</th>
                    <th className="text-left px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {appts.map((a) => (
                    <tr key={a.id} className="border-t border-border" data-testid={`admin-appt-${a.id}`}>
                      <td className="px-5 py-3 text-ink">{a.user_name || "—"}</td>
                      <td className="px-5 py-3 text-ink-soft">{a.accompagnateur_name}</td>
                      <td className="px-5 py-3 text-ink-soft">{a.date} · {a.time}</td>
                      <td className="px-5 py-3 text-ink-soft">{a.duration_min} min</td>
                      <td className="px-5 py-3"><span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_STYLE[a.status]}`}>{STATUS_LABEL[a.status]}</span></td>
                      <td className="px-5 py-3 text-sage-700 font-medium">{formatXOF(a.price)}</td>
                      <td className="px-5 py-3">
                        <select value={a.status} onChange={(e) => setStatus(a.id, e.target.value)} className="text-xs border border-border rounded-lg px-2 py-1 bg-white" data-testid={`admin-status-${a.id}`}>
                          <option value="pending">En attente</option>
                          <option value="confirmed">Confirmer</option>
                          <option value="completed">Terminer</option>
                          <option value="cancelled">Annuler</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <div className="card-soft p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-beige/60 text-ink-soft uppercase text-xs tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Client</th>
                    <th className="text-left px-5 py-3">Méthode</th>
                    <th className="text-left px-5 py-3">Référence</th>
                    <th className="text-left px-5 py-3">Montant</th>
                    <th className="text-left px-5 py-3">Statut</th>
                    <th className="text-left px-5 py-3">Date</th>
                    <th className="text-left px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-ink-mute">Aucun paiement déclaré.</td></tr>
                  ) : payments.map((p) => (
                    <tr key={p.id} className="border-t border-border" data-testid={`admin-payment-${p.id}`}>
                      <td className="px-5 py-3 text-ink">{p.user_name || "—"}</td>
                      <td className="px-5 py-3 text-ink-soft uppercase text-xs">{p.method}</td>
                      <td className="px-5 py-3 text-ink-soft font-mono text-xs">{p.reference || "—"}</td>
                      <td className="px-5 py-3 text-sage-700 font-medium">{formatXOF(p.amount)}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full ${
                          p.status === "validated" ? "bg-sage-50 text-sage-700 border border-sage-200" :
                          p.status === "refused" ? "bg-red-50 text-red-700 border border-red-200" :
                          "bg-gold-light/40 text-gold-dark border border-gold/40"
                        }`}>{p.status}</span>
                      </td>
                      <td className="px-5 py-3 text-ink-mute text-xs">{p.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="px-5 py-3">
                        {p.status === "declared" ? (
                          <div className="flex gap-1">
                            {(() => {
                              const n = notifs.find((x) => x.payment_id === p.id);
                              return n?.whatsapp_link ? (
                                <a href={n.whatsapp_link} target="_blank" rel="noreferrer" className="btn-pill bg-[#25D366] text-white px-3 py-1 text-xs hover:bg-[#1bb056]" data-testid={`payment-wa-${p.id}`}>
                                  <MessageCircle className="w-3 h-3" />
                                </a>
                              ) : null;
                            })()}
                            <button onClick={() => validatePayment(p.id, "validated")} className="btn-pill bg-sage-500 text-white px-3 py-1 text-xs hover:bg-sage-600" data-testid={`payment-validate-${p.id}`}>
                              <Check className="w-3 h-3" /> Valider
                            </button>
                            <button onClick={() => validatePayment(p.id, "refused")} className="btn-pill border border-border text-ink-soft px-3 py-1 text-xs hover:border-red-300 hover:text-red-600" data-testid={`payment-refuse-${p.id}`}>
                              <XCircle className="w-3 h-3" /> Refuser
                            </button>
                          </div>
                        ) : <span className="text-xs text-ink-mute">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="card-soft p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-beige/60 text-ink-soft uppercase text-xs tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Nom</th>
                    <th className="text-left px-5 py-3">Email</th>
                    <th className="text-left px-5 py-3">Rôle</th>
                    <th className="text-left px-5 py-3">Pays</th>
                    <th className="text-left px-5 py-3">Inscription</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-border" data-testid={`admin-user-${u.id}`}>
                      <td className="px-5 py-3 text-ink">{u.name}</td>
                      <td className="px-5 py-3 text-ink-soft">{u.email}</td>
                      <td className="px-5 py-3"><span className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-sage-50 text-sage-700 border border-sage-200">{u.role}</span></td>
                      <td className="px-5 py-3 text-ink-soft">{u.country || "—"}</td>
                      <td className="px-5 py-3 text-ink-mute">{u.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
